// we need this instead of expected() to GO FAST!!
import { ToolkitListingID, isUndefined } from '@aloft/toolkit-shared';
import distance from '@turf/distance';

import {
  DifferenceCriteriaItem,
  MatchCriteria,
  MatchCriteriaItem,
  MatchProperty,
  MatchSearchInput,
  MatchSearchOutput,
  MatchSearchResult,
  MatchSearchStats,
} from './types.ts';

type KeyedMatchProperty = [ToolkitListingID, MatchProperty];

type Predicate1 = (p: MatchProperty) => boolean;
type Predicate2 = (p1: MatchProperty, p2: MatchProperty) => boolean;

const maxResults = 100;

export const runSearch = (input: MatchSearchInput): MatchSearchOutput => {
  const properties = Object.entries(input.properties) as KeyedMatchProperty[];

  const diff1 = compileDifference(input.difference.p1);
  const diff2 = compileDifference(input.difference.p2);
  const match = compileMatch(input.match);

  const stats: MatchSearchStats = {
    isTruncated: false,
    numTotalMatches: 0,
    numTotalPairsTested: 0,
  };

  const set1 = properties.filter(([, p]) => diff1(p));
  const set2 = properties.filter(([, p]) => diff2(p));

  const pairs: MatchSearchResult[] = [];
  const pairIndex: Map<ToolkitListingID, Set<ToolkitListingID>> = new Map();
  const simVectors: Map<ToolkitListingID, SimVector> = new Map();

  for (const [key, prop] of properties) simVectors.set(key, propertyToSimVector(prop));
  const popStats = collectPopulationStatistics([...simVectors.values()]);

  for (const [k1, p1] of set1) {
    const edgeIndex = pairIndex.get(k1) ?? new Set();
    pairIndex.set(k1, edgeIndex);

    for (const [k2, p2] of set2) {
      if (k1 === k2 || pairIndex.get(k2)?.has(k1)) continue;
      edgeIndex.add(k2);

      stats.numTotalPairsTested += 1;
      if (!match(p1, p2)) continue;
      const distance = simVectorDistance(popStats, simVectors.get(k1)!, simVectors.get(k2)!);

      stats.numTotalMatches += 1;
      pairs.push({ p1: k1, p2: k2, distance });
    }
  }

  pairs.sort((a, b) => a.distance - b.distance);
  if (pairs.length > maxResults) {
    pairs.length = maxResults;
    stats.isTruncated = true;
  }

  return { pairs, stats };
};

const toLowerCase = (x: unknown): string => (typeof x === 'string' ? x.toLowerCase() : '');

const compileDifference = (criteria: DifferenceCriteriaItem): Predicate1 => {
  const { t, key, value } = criteria;
  switch (t) {
    case 'any':
      return () => true;
    case 'includeKeyword':
      return (p) => !!p.marketingCommentary?.toLowerCase?.()?.includes?.(toLowerCase(value));
    case 'omitKeyword':
      return (p) => !p.marketingCommentary?.toLowerCase?.()?.includes?.(toLowerCase(value));
    case 'address':
      return (p) => !!p.address?.toLowerCase?.()?.includes?.(toLowerCase(value));
    case 'equal':
      return (p) => key !== undefined && p[key] === value;
    default:
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`don't know how to ${t}`);
  }
};

const compileMatch = (criteria: MatchCriteria): Predicate2 => {
  const fns: Predicate2[] = [addressNotEqual, partitionKeyNotEqual];
  const cr = Object.entries(criteria) as [keyof MatchProperty, MatchCriteriaItem | undefined][];

  for (const [key, item] of cr) {
    if (item !== undefined && item.t !== 'any') {
      fns.push(compileMatchItem(key, item));
    }
  }

  return (p1, p2) => fns.every((f) => f(p1, p2));
};

const addressNotEqual = (p1: MatchProperty, p2: MatchProperty): boolean =>
  p1.address !== p2.address;

const partitionKeyNotEqual = (p1: MatchProperty, p2: MatchProperty): boolean =>
  isUndefined(p1.partitionKey) ||
  isUndefined(p2.partitionKey) ||
  p1.partitionKey !== p2.partitionKey;

const compileMatchItem = (key: keyof MatchProperty, criteria: MatchCriteriaItem): Predicate2 => {
  const { t, value } = criteria;
  switch (t) {
    case 'any':
      return () => true;
    case 'equal':
      return (p1, p2) => p1[key] === p2[key] && p1[key] !== undefined;
    case 'less':
      return key === 'coords'
        ? (p1, p2) => proximity(p1, p2) < value
        : (p1, p2) => +p1[key]! < value && +p2[key]! < value;
    case 'greater':
      return key === 'coords'
        ? (p1, p2) => proximity(p1, p2) > value
        : (p1, p2) => +p1[key]! > value && +p2[key]! > value;
    case 'withinUnit':
      return (p1, p2) => Math.abs(+p1[key]! - +p2[key]!) <= value;
    case 'withinPercent':
      return (p1, p2) => {
        const x1 = +p1[key]!;
        const x2 = +p2[key]!;
        // the choice to use min() or max() here is essentially
        // arbitrary, but we need to choose one or the other so the
        // condition is symmetric
        return Math.abs(x1 - x2) / Math.max(x1, x2) <= value;
      };
    default:
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`don't know how to ${t}`);
  }
};

const simVectorDim = 10;
const simVectorZero: SimVector = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
type SimVector = [
  number, // salePrice
  number, // bedroom
  number, // bathroom
  number, // halfBath
  number, // stories
  number, // attachedGarageParkingSpacesCount
  number, // glaSqFt
  number, // siteSizeSqFt
  number, // yearBuilt
  number, // contractDate
  number, // saleDate
];
type PopStats = {
  average: SimVector;
  variance: SimVector;
  stddev: SimVector;
};
const simVectorWeight = [
  3, // salePrice
  2, // bedroom
  2, // bathroom
  1, // halfBath
  1, // stories
  1, // attachedGarageParkingSpacesCount
  45, // glaSqFt
  25, // siteSizeSqFt
  15, // yearBuilt
  3, // contractDate
  3, // saleDate
];

const propertyToSimVector = (prop: MatchProperty): SimVector => [
  prop.salePrice ?? NaN,
  prop.bedrooms ?? NaN,
  prop.bathrooms ?? NaN,
  prop.halfBathrooms ?? NaN,
  prop.stories ?? NaN,
  prop.attachedGarageParkingSpacesCount ?? NaN,
  Math.log(prop.glaSqFt ?? NaN),
  Math.log(prop.siteSizeSqFt ?? NaN),
  prop.yearBuilt ?? NaN,
  prop.contractDate ?? NaN,
  prop.saleDate ?? NaN,
];

const collectPopulationStatistics = (properties: SimVector[]): PopStats => {
  const average: SimVector = [...simVectorZero];
  const variance: SimVector = [...simVectorZero];
  const stddev: SimVector = [...simVectorZero];

  for (let i = 0; i < simVectorDim; i++) {
    let count = 0;
    let total = 0;

    for (const prop of properties) {
      const x = prop[i]!;

      if (!Number.isNaN(x)) {
        total += x;
        count += 1;
      }
    }

    average[i] = count === 0 ? 0 : total / count;
    const avg = average[i]!;
    total = 0;

    for (const prop of properties) {
      const x = prop[i]!;
      if (!Number.isNaN(x)) total += (x - avg) * (x - avg);
    }

    variance[i] = count === 0 ? 0 : total / count;
    const va = variance[i]!;
    stddev[i] = Math.sqrt(va);
  }

  return { average, variance, stddev };
};

const simVectorDistance = ({ stddev }: PopStats, a: SimVector, b: SimVector): number => {
  let total = 0;

  for (let i = 0; i < simVectorDim; i++) {
    const d = Math.abs(a[i]! - b[i]!) / stddev[i]!;
    total += simVectorWeight[i]! * (Number.isNaN(d) ? 1 : d);
  }

  return total;
};

const proximity = (p1: MatchProperty, p2: MatchProperty): number =>
  !p1.coords || !p2.coords ? NaN : distance(p1.coords, p2.coords, { units: 'miles' });
